/**
 * @fileoverview `ArtistTourHistory` — past-events list for an artist's city profile.
 *
 * Composes `TourHistoryRow` molecules. Renders an empty-state when no past events
 * exist. Long lists collapse to the first INITIAL_LIMIT rows with a "Show more"
 * toggle.
 *
 * View + Connected split: `ArtistTourHistoryView` is pure props for tests + Storybook.
 * `ArtistTourHistory` is the connected form fed by `fetchArtistDetail`.
 *
 * @since WEB-105
 */
'use client';

import * as React from 'react';
import { TourHistoryRow } from '@/components/molecules/TourHistoryRow';
import { cn } from '@/components/atoms/primitives/utils';
import type { ArtistCityEvent } from '@/types/artistDetail';

const INITIAL_LIMIT = 5;
const HEADING_ID = 'artist-tour-history-heading';

export interface ArtistTourHistoryViewProps {
  artistName: string;
  pastEvents: readonly ArtistCityEvent[];
  cityLabel: string;
  className?: string;
}

export function ArtistTourHistoryView({
  artistName,
  pastEvents,
  cityLabel,
  className,
}: ArtistTourHistoryViewProps): React.ReactElement {
  const [expanded, setExpanded] = React.useState(false);
  const visible = expanded ? pastEvents : pastEvents.slice(0, INITIAL_LIMIT);
  const hasOverflow = pastEvents.length > INITIAL_LIMIT;

  return (
    <section
      aria-labelledby={HEADING_ID}
      className={cn('flex flex-col gap-4', className)}
      data-testid="artist-tour-history"
    >
      <h2 id={HEADING_ID} className="text-2xl font-semibold tracking-tight text-foreground">
        {artistName} tour history
        {pastEvents.length > 0 ? ` (${pastEvents.length})` : ''}
      </h2>
      {pastEvents.length === 0 ? (
        <p className="text-sm text-muted-foreground">No past shows in {cityLabel} yet.</p>
      ) : (
        <>
          <div role="list">
            {visible.map((event) => (
              <div role="listitem" key={event.id}>
                <TourHistoryRow event={event} />
              </div>
            ))}
          </div>
          {hasOverflow && (
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              className="self-start min-h-11 desktop:min-h-0 rounded-sm text-sm font-medium text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              aria-expanded={expanded}
            >
              {expanded ? 'Show less' : `Show more (${pastEvents.length - INITIAL_LIMIT})`}
            </button>
          )}
        </>
      )}
    </section>
  );
}

export interface ArtistTourHistoryProps {
  artistName: string;
  pastEvents: readonly ArtistCityEvent[];
  cityLabel: string;
  className?: string;
}

export function ArtistTourHistory(props: ArtistTourHistoryProps): React.ReactElement {
  return <ArtistTourHistoryView {...props} />;
}
